import {isSuperAdminAuthority, isCompanyGroupAdminAuthority} from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

/** Environment capabilities served by `GET /{admin,system,groupAdmin}/config`. */
export interface ClientConfig {
    directVideoUploadEnabled: boolean;
}

/**
 * The same handler, reached on whichever URL space this persona is allowed into.
 *
 * <p>A company-less persona has no company surface: the server floors `/admin/**` to ADMIN/TRAINER, so a
 * super-admin or group admin asking there gets a 403, which this service reads as "feature off" and hands
 * the caller the legacy upload path. That is how a super-admin came to stream a 118 MB file through the
 * server — nothing failed, it just quietly did the slow thing.
 *
 * <p>Impersonation needs no case of its own: the authorities mirrored here are the impersonated identity's,
 * so an impersonating super-admin is an ADMIN and takes the company path, exactly as a direct company login
 * does.
 */
const configPath = (): string => {
    if (isSuperAdminAuthority()) return '/system/config';
    if (isCompanyGroupAdminAuthority()) return '/groupAdmin/config';
    return '/admin/config';
};

/**
 * What an environment we could not ask about is assumed to support: nothing new.
 *
 * <p>Failing closed is the only safe direction. The server independently refuses to open a direct
 * upload when its own flag is off, so guessing "on" turns a network blip into an upload the user
 * watches fail, while guessing "off" costs them a slower upload that still works.
 */
const CONSERVATIVE_DEFAULT: ClientConfig = {directVideoUploadEnabled: false};

/**
 * In-flight or resolved fetch, shared by every caller.
 *
 * <p>The promise itself is cached rather than its result, so several components mounting at once
 * produce one request instead of a burst. These are environment-wide switches that only change on
 * deploy, so a value read once per page load is not stale in any way that matters.
 */
let inFlight: Promise<ClientConfig> | null = null;

const fetchConfig = async (): Promise<ClientConfig> => {
    const token = localStorage.getItem('auth');
    const response = await fetch(`${API_BASE}${configPath()}`, {
        headers: token ? {Authorization: `Bearer ${token}`} : {},
    });

    if (!response.ok) {
        throw new Error(`Config request failed with ${response.status}`);
    }

    const payload = await response.json();
    return {
        // Strictly a boolean true, so an older server that does not send the field — or one that sends
        // it as a string — lands on the old path rather than on a truthiness accident.
        directVideoUploadEnabled: payload?.body?.directVideoUploadEnabled === true,
    };
};

/** The environment's capabilities, fetched at most once per page load. */
export const getClientConfig = (): Promise<ClientConfig> => {
    if (!inFlight) {
        inFlight = fetchConfig().catch(error => {
            console.warn(`[ClientConfigService] Could not read ${configPath()}; assuming the pre-existing `
                + 'behaviour', error);
            // Cleared so a later attempt can succeed: a failure here should not pin the whole session to
            // the fallback because one request happened to land during a deploy.
            inFlight = null;
            return CONSERVATIVE_DEFAULT;
        });
    }
    return inFlight;
};

/** Test seam — lets a suite start from a known state rather than a previous test's cache. */
export const resetClientConfigCache = (): void => {
    inFlight = null;
};

export default {getClientConfig, resetClientConfigCache};
